import { jsx as _jsx } from "react/jsx-runtime";
import Markdown from 'react-markdown';
import { Fragment } from 'react';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { MARKDOWN_SANITIZE_SCHEMA } from '../primitives/markdown.js';
/**
 * The only elements InlineMarkdown may emit. Everything else (lists, headings,
 * tables, blockquotes…) is disallowed and unwrapped to its plain text children
 * via `unwrapDisallowed`, so block markdown degrades to text instead of
 * producing block tags inside inline containers (`<span>`, `<p>`), which would
 * be invalid HTML.
 */
const INLINE_ALLOWED_ELEMENTS = ['p', 'strong', 'em', 'del', 'code', 'a', 'br'];
/**
 * Inline-only styling map. `p` is unwrapped to a fragment so the rendered
 * output stays inline (no block margins) and inherits the parent element's
 * text color and size — only genuinely inline marks (`strong`, `em`, `code`,
 * `a`, `del`) carry any styling of their own.
 */
const INLINE_COMPONENTS = {
    p: ({ children }) => _jsx(Fragment, { children: children }),
    strong: ({ children }) => _jsx("strong", { className: "font-semibold", children: children }),
    em: ({ children }) => _jsx("em", { className: "italic", children: children }),
    del: ({ children }) => _jsx("del", { className: "opacity-70", children: children }),
    code: ({ children }) => (_jsx("code", { className: "rounded bg-muted px-1 py-0.5 font-mono text-[0.9em] text-foreground", children: children })),
    a: ({ href, children }) => (_jsx("a", { href: href, target: "_blank", rel: "noopener noreferrer nofollow", className: "text-primary underline underline-offset-4 hover:no-underline", children: children })),
};
/**
 * Variant of {@link INLINE_COMPONENTS} for content that already sits inside an
 * anchor (e.g. item-list titles): markdown links render as plain styled text
 * instead of `<a>`, since nested anchors are invalid HTML and make click
 * behavior ambiguous.
 */
const INLINE_COMPONENTS_NO_LINKS = {
    ...INLINE_COMPONENTS,
    a: ({ children }) => _jsx("span", { className: "underline underline-offset-4", children: children }),
};
/**
 * Renders a short digest string with inline markdown (`**bold**`, `` `code` ``,
 * `[links](…)`, `_em_`, `~~del~~`) applied. Digest bodies and item details
 * carry inline markdown that would otherwise render as literal asterisks and
 * backticks.
 *
 * Only inline elements are emitted ({@link INLINE_ALLOWED_ELEMENTS}); block
 * markdown is unwrapped to its text children. Pass `linksAsText` when the
 * output is rendered inside an existing `<a>` so markdown links can't nest
 * anchors.
 *
 * Uses the same {@link MARKDOWN_SANITIZE_SCHEMA} as the block `markdown`
 * primitive (agent-supplied URLs restricted to `http`/`https`/`mailto`); never
 * `dangerouslySetInnerHTML`. Output is inline and inherits the parent's text
 * color and size — style it by styling the wrapping element.
 */
export function InlineMarkdown({ children, linksAsText = false, }) {
    return (_jsx(Markdown, { remarkPlugins: [remarkGfm], rehypePlugins: [[rehypeSanitize, MARKDOWN_SANITIZE_SCHEMA]], allowedElements: INLINE_ALLOWED_ELEMENTS, unwrapDisallowed: true, components: linksAsText ? INLINE_COMPONENTS_NO_LINKS : INLINE_COMPONENTS, children: children }));
}
